import React from "react";
import { Edit2, Trash2, Send, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import type { PackageFormInput } from "./AddPackageModal";
import { useAuth } from "../../auth/AuthContext";

type Package = PackageFormInput;

interface SunbedPackagesTableProps {
  packages: Package[];
  isLoading: boolean;
  indexOfFirstItem: number;
  onEdit: (pkg: Package) => void;
  onDelete: (id: string) => void;
  onReminder: (pkg: Package) => void;
}

export const SunbedPackagesTable: React.FC<SunbedPackagesTableProps> = ({
  packages,
  isLoading,
  indexOfFirstItem,
  onEdit,
  onDelete,
  onReminder,
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold">#</th>
            <th className="col-span-2 text-left font-bold">Label</th>
            <th className="col-span-3 text-left font-bold">Rooms</th>
            <th className="col-span-4 text-left font-bold">Pricing</th>
            <th className="col-span-1 text-center font-bold">Status</th>
            <th className="col-span-1 text-right font-bold">Actions</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {isLoading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : packages.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">
                  No packages found
                </p>
                <p className="text-sm">Try adjusting your search terms</p>
              </td>
            </tr>
          ) : (
            <>
              {packages.map((pkg, idx) => (
                <tr
                  key={pkg.id || idx}
                  className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white group text-sm w-full"
                >
                  <td className="col-span-1 text-gray-500 font-medium">
                    {indexOfFirstItem + idx + 1}
                  </td>
                  <td className="col-span-2 font-semibold text-gray-800 group-hover:text-[#885ABB] transition-colors">
                    {pkg.label}
                  </td>
                  <td className="col-span-3 flex flex-wrap gap-1.5">
                    {pkg.sunbed_room.slice(0, 4).map((room) => {
                      const label =
                        typeof room === "string" ? room : room.label;
                      return (
                        <span
                          key={label}
                          className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-[#FFF8E1] text-[#F9A825] border border-[#FFE082]"
                        >
                          {label}
                        </span>
                      );
                    })}
                    {pkg.sunbed_room.length > 4 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors">
                            +{pkg.sunbed_room.length - 4} more
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-3">
                          <div className="space-y-2">
                            <h4 className="font-medium text-xs text-gray-500 uppercase tracking-wider">
                              Remaining Rooms
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {pkg.sunbed_room.slice(4).map((room) => {
                                const label =
                                  typeof room === "string" ? room : room.label;
                                return (
                                  <span
                                    key={label}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-[#FFF8E1] text-[#F9A825] border border-[#FFE082]"
                                  >
                                    {label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </td>
                  <td className="col-span-4 flex flex-wrap gap-1.5">
                    {pkg.pricing.slice(0, 3).map((p) => (
                      <span
                        key={p.id}
                        className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-[#F3E8FF] text-[#885ABB] border border-[#C4A6E1]"
                      >
                        {p.label} - {p.type == "per_minute" ? "Per min" : "Fixed"}
                      </span>
                    ))}
                    {pkg.pricing.length > 3 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-600 border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors">
                            +{pkg.pricing.length - 3}
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-3">
                          <div className="space-y-2">
                            <h4 className="font-medium text-xs text-gray-500 uppercase tracking-wider">
                              Additional Pricing
                            </h4>
                            <div className="flex flex-col gap-2">
                              {pkg.pricing.slice(3).map((p) => (
                                <div
                                  key={p.id}
                                  className="flex items-center justify-between px-2 py-1.5 rounded bg-gray-50 border border-gray-100"
                                >
                                  <span className="text-xs font-medium text-gray-700">
                                    {p.label} - {p.type == "per_minute" ? "Per min" : "Fixed"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </td>
                  <td className="col-span-1 text-center">
                    <span
                      className={cn(
                        "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                        pkg.status === "active"
                          ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]"
                          : "bg-gray-100 text-gray-600 border-gray-200"
                      )}
                    >
                      {pkg.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="col-span-1 flex justify-end gap-2 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onReminder(pkg)}
                      className="h-8 w-8 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                      title="Send Reminder"
                    >
                      <Send size={15} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onEdit(pkg)}
                      className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Edit2 size={15} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onDelete(pkg.id!)}
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
